
import React from "react";
import SectionHeader from "./SectionHeader";
import { Separator } from "@/components/ui/separator";
import { AlertTriangle } from "lucide-react";

interface ObjectionItem {
  objection: string;
  rebuttal: string;
}

interface ObjectionsSectionProps {
  objections: string;
  isLast?: boolean;
}

const ObjectionsSection: React.FC<ObjectionsSectionProps> = ({ 
  objections,
  isLast = false 
}) => {
  // Check if we have any objections to display
  if (!objections || objections === "No objections provided.") {
    return (
      <>
        <div>
          <SectionHeader number={6} title="Addressing Common Objections" />
          <div className="prose prose-lg max-w-none text-left">
            <p className="text-[#403E43] italic">
              No objections were provided for this question. You can ask a follow-up question specifically about common objections to this topic.
            </p>
          </div>
        </div>
        {!isLast && <Separator className="bg-heaven-gold/10" />}
      </>
    );
  }
  
  // Parse objections into structured format with improved handling
  const objectionItems = parseObjections(objections);

  // If parsing failed or returned no valid results, display the raw text with basic formatting
  if (!objectionItems || objectionItems.length === 0) {
    return (
      <>
        <div>
          <SectionHeader number={6} title="Addressing Common Objections" />
          <div className="prose prose-lg max-w-none text-left">
            <p className="text-[#403E43] whitespace-pre-line">{formatRawObjections(objections)}</p>
          </div>
        </div>
        {!isLast && <Separator className="bg-heaven-gold/10" />}
      </>
    );
  }

  // If we have structured objections, display them in a nice format
  return (
    <>
      <div>
        <SectionHeader number={6} title="Addressing Common Objections" />
        <div className="prose prose-lg max-w-none text-left">
          <div className="space-y-6">
            {objectionItems.map((item, index) => (
              <div key={index} className="space-y-3">
                <div className="bg-heaven-gold/5 p-4 rounded-md">
                  <p className="font-semibold text-heaven-gold">
                    {`Objection ${index + 1}: ${item.objection}`}
                  </p>
                </div>
                <div className="p-4 bg-heaven-gold/2 border-l-4 border-heaven-gold ml-4">
                  <p className="font-semibold text-heaven-gold">Response:</p>
                  <p className="text-[#403E43] whitespace-pre-line">{item.rebuttal}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {!isLast && <Separator className="bg-heaven-gold/10" />}
    </>
  );
};

/**
 * Format raw objection text for display when structured parsing fails
 */
function formatRawObjections(text: string): string {
  if (!text) return '';
  
  // Remove duplicate sections if detected
  const objectionMarkers = text.match(/Objection\s+\d+:|Objection:/gi) || [];
  const uniqueMarkers = [...new Set(objectionMarkers.map(m => m.toLowerCase()))];
  
  let formattedText = text;
  
  // If we detect duplicate markers, try to clean up the text
  if (objectionMarkers.length > uniqueMarkers.length) {
    const firstCompleteSetEnd = findEndOfFirstCompleteSet(text);
    if (firstCompleteSetEnd > 0) {
      formattedText = text.substring(0, firstCompleteSetEnd);
    }
  }
  
  // Clean up the text
  return formattedText
    .replace(/\\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/\*\*/g, '')
    .replace(/#/g, '')
    .replace(/Objection\s+\d+:/g, '\n**Objection:**')
    .replace(/Response\s+\d+:/g, '\n**Response:**')
    .replace(/Objection:/g, '\n**Objection:**')
    .replace(/Response:/g, '\n**Response:**')
    .trim();
}

/**
 * Find the end of the first complete set of objections
 */
function findEndOfFirstCompleteSet(text: string): number {
  const objectionMatches = Array.from(text.matchAll(/Objection\s+(\d+):|Objection:/gi));
  const responseMatches = Array.from(text.matchAll(/Response\s+(\d+):|Response:/gi));
  
  // If we have a sequence of objection-response pairs, look for repetition
  if (objectionMatches.length >= 2) {
    // Extract objection numbers if available
    const objectionNumbers = [];
    for (const match of objectionMatches) {
      const numMatch = match[0].match(/\d+/);
      if (numMatch) {
        objectionNumbers.push(parseInt(numMatch[0], 10));
      }
    }
    
    // If we have numbered objections, check for repetition in the sequence
    if (objectionNumbers.length >= 2) {
      for (let i = 1; i < objectionNumbers.length; i++) {
        // If we see a number that's less than or equal to an earlier one, it's likely a repetition
        for (let j = 0; j < i; j++) {
          if (objectionNumbers[i] === objectionNumbers[j]) {
            // Return the position where the repetition starts
            return objectionMatches[i].index || 0;
          }
        }
      }
    }
  }
  
  // Also check for repeating section headers that might indicate duplication
  const sectionMatches = Array.from(text.matchAll(/(?:Final Encouragement|Call to Faithfulness):/gi));
  if (sectionMatches.length > 1 && sectionMatches[1].index) {
    return sectionMatches[1].index;
  }
  
  return -1;
}

/**
 * Parse objections into structured format with improved deduplication
 */
function parseObjections(text: string): ObjectionItem[] {
  if (!text || text.length < 50) return [];
  
  const objectionItems: ObjectionItem[] = [];
  const seenObjections = new Set<string>();
  
  try {
    // Method 1: Try to match patterns like "Objection 1: ... Response: ..."
    const objectionPattern1 = /Objection\s+\d*:?\s*([^\n]+)(?:\n|.*?Response:?|.*?Rebuttal:?)\s*([^]*?)(?=(?:\n\s*Objection\s+\d*:|Final Encouragement|$))/gi;
    let match;
    
    while ((match = objectionPattern1.exec(text)) !== null) {
      if (match[1] && match[2]) {
        const objection = cleanText(match[1]);
        const rebuttal = cleanText(match[2]);
        
        // Create fingerprint for deduplication (normalize text by removing numbers and excess spaces)
        const objFingerprint = objection.toLowerCase().replace(/\d+/g, '').replace(/\s+/g, ' ');
        
        if (!seenObjections.has(objFingerprint) && objection && rebuttal) {
          seenObjections.add(objFingerprint);
          objectionItems.push({ objection, rebuttal });
        }
      }
    }
    
    // If Method 1 didn't find any objections, try another pattern
    if (objectionItems.length === 0) {
      // Method 2: Try to find sections labeled "Objection:" and "Response:"
      const objectionPattern2 = /Objection(?:\s+\d+)?:?\s*([^\n]+)(?:\n|.*?)(?:Response|Rebuttal):?\s*([^]*?)(?=(?:\n\s*Objection(?:\s+\d+)?:|Final Encouragement|$))/gi;
      
      while ((match = objectionPattern2.exec(text)) !== null) {
        if (match[1] && match[2]) {
          const objection = cleanText(match[1]);
          const rebuttal = cleanText(match[2]);
          
          // Check for duplicates with fingerprinting
          const objFingerprint = objection.toLowerCase().replace(/\d+/g, '').replace(/\s+/g, ' ');
          
          if (!seenObjections.has(objFingerprint) && objection && rebuttal) {
            seenObjections.add(objFingerprint);
            objectionItems.push({ objection, rebuttal });
          }
        }
      }
    }
    
    // Method 3: If still no results, try splitting by paragraphs and looking for patterns
    if (objectionItems.length === 0) {
      const paragraphs = text.split(/\n\s*\n/).filter(p => p.trim().length > 0);
      
      // Detect if paragraphs alternate between objection and response
      let isObjectionFirst = false;
      
      // Check first paragraph to see if it looks like an objection
      if (paragraphs.length > 0) {
        isObjectionFirst = paragraphs[0].includes('?') || 
                          /atheist|skeptic|critic|object|challenge/i.test(paragraphs[0]);
      }
      
      // Try to pair paragraphs as objection-response
      for (let i = 0; i < paragraphs.length - 1; i += 2) {
        const objIndex = isObjectionFirst ? i : i + 1;
        const respIndex = isObjectionFirst ? i + 1 : i;
        
        if (objIndex < paragraphs.length && respIndex < paragraphs.length) {
          const objection = cleanText(paragraphs[objIndex]);
          const rebuttal = cleanText(paragraphs[respIndex]);
          
          // Only add if both parts are substantial
          if (objection.length > 10 && rebuttal.length > 10) {
            const objFingerprint = objection.toLowerCase().replace(/\d+/g, '').replace(/\s+/g, ' ');
            
            if (!seenObjections.has(objFingerprint)) {
              seenObjections.add(objFingerprint);
              objectionItems.push({ objection, rebuttal });
            }
          }
        }
      }
    }
    
    return objectionItems;
  } catch (error) {
    console.error("Error parsing objections:", error);
    return [];
  }
}

/**
 * Clean up text for objections and rebuttals
 */
function cleanText(text: string): string {
  if (!text) return '';
  
  return text
    .replace(/^\s*\*\*\s*/, '')      // Remove leading **
    .replace(/\s*\*\*\s*$/, '')      // Remove trailing **
    .replace(/^\d+\.\s*/, '')        // Remove leading number with dot
    .replace(/^[•-]\s*/, '')         // Remove bullet points
    .replace(/Response\s+\d+:\s*/i, '') // Remove "Response X:" prefix
    .replace(/Rebuttal\s+\d+:\s*/i, '') // Remove "Rebuttal X:" prefix
    .replace(/Response:\s*/i, '')    // Remove "Response:" prefix
    .replace(/Rebuttal:\s*/i, '')    // Remove "Rebuttal:" prefix
    .trim();
}

export default ObjectionsSection;
